import { useSEO } from '@/hooks/useSEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TwoFactorAuth } from '@/components/TwoFactorAuth';
import { ChangePassword } from '@/components/ChangePassword';
import { LocationSharingSettings } from '@/components/LocationSharingSettings';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Shield, Lock, Activity } from 'lucide-react';

const SecuritySettings = () => {
  const { user, session } = useAuth();
  const navigate = useNavigate();

  useSEO({
    title: 'Security Settings - ChessHatch',
    description: 'Manage your account security settings including two-factor authentication',
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  if (!user) return null;

  const sessionExpiresAt = session?.expires_at
    ? new Date(session.expires_at * 1000).toLocaleString()
    : 'Unknown';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950/95 to-slate-950">
      <div className="container max-w-4xl mx-auto py-8 px-4 space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8 text-primary" />
            Security Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your account security and authentication preferences
          </p>
        </div>

        <div className="grid gap-6">
          <TwoFactorAuth />

          <ChangePassword />

          <LocationSharingSettings />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Session Information
              </CardTitle>
              <CardDescription>
                Your current login session details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Session Status</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    Active
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Session Expires</span>
                  <span className="text-sm font-medium">{sessionExpiresAt}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Email</span>
                  <span className="text-sm font-medium">{user.email}</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Security Tip:</strong> You'll receive a warning 5 minutes before your session expires.
                  Enable 2FA for additional security on your account.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Security Best Practices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Use a unique, strong password with at least 12 characters</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Enable two-factor authentication for extra protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Review login activity regularly for suspicious access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Never share your password or 2FA codes with anyone</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Log out from public or shared devices after use</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
